import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The «Sign in with Google» button, the same for bench and office.
 *
 * It uses Google Identity Services: Google's script draws the button and, when
 * the person picks the account, hands over a **credential** (a JWT signed by
 * Google) that the API verifies. No redirect: the page stays where it is.
 *
 * `subito` also opens Google's prompt without waiting for a tap on the button.
 * It is for confirming signed gestures: with Chrome already in the company
 * account, confirming becomes a tap instead of a password. Not on the sign-in
 * screen, though: after «Esci», whoever comes next must not find themselves
 * signed in with the previous person's account.
 */
import { useEffect, useRef, useState } from 'react';
import { configurazioneAccesso } from './transport';
const SCRIPT = 'https://accounts.google.com/gsi/client';
function googleId() {
    const g = window.google;
    return g?.accounts?.id ?? null;
}
let caricamento = null;
/** The script is added once, even with several buttons on the page. */
function caricaGoogle() {
    const pronto = googleId();
    if (pronto)
        return Promise.resolve(pronto);
    if (caricamento)
        return caricamento;
    caricamento = new Promise((risolvi, rifiuta) => {
        const script = document.createElement('script');
        script.src = SCRIPT;
        script.async = true;
        script.onload = () => {
            const id = googleId();
            if (id)
                risolvi(id);
            else
                rifiuta(new Error('lo script di Google non ha risposto'));
        };
        script.onerror = () => {
            // try again next time instead of staying broken until a reload
            caricamento = null;
            script.remove();
            rifiuta(new Error('Google non raggiungibile'));
        };
        document.head.appendChild(script);
    });
    return caricamento;
}
export function BottoneGoogle({ clientId, allaCredenziale, subito = false, }) {
    const posto = useRef(null);
    // the latest callback, without redrawing the button on every render
    const consegna = useRef(allaCredenziale);
    useEffect(() => {
        consegna.current = allaCredenziale;
    }, [allaCredenziale]);
    useEffect(() => {
        let vivo = true;
        caricaGoogle()
            .then((id) => {
            if (!vivo || !posto.current)
                return;
            id.initialize({
                client_id: clientId,
                callback: (r) => consegna.current(r.credential),
                auto_select: subito,
                cancel_on_tap_outside: false,
            });
            id.renderButton(posto.current, {
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                locale: 'it',
            });
            if (subito)
                id.prompt();
        })
            .catch(() => {
            // without Google the rest of the screen remains: whoever has a password gets in
        });
        return () => {
            vivo = false;
        };
    }, [clientId, subito]);
    return _jsx("div", { ref: posto });
}
/** Call on sign-out: Google must not reopen by itself the session of whoever left. */
export function dimenticaSceltaGoogle() {
    googleId()?.disableAutoSelect();
}
/**
 * Google's client ID, asked of the API once per screen.
 *
 * `undefined` until it answers, `null` if Google is not configured — in
 * development and tests, where sign-in is by password as always — and also if
 * the API does not answer: better the password screen than none.
 */
export function useGoogleClientId() {
    const [clientId, setClientId] = useState(undefined);
    useEffect(() => {
        let vivo = true;
        configurazioneAccesso()
            .then((c) => vivo && setClientId(c.googleClientId))
            .catch(() => vivo && setClientId(null));
        return () => {
            vivo = false;
        };
    }, []);
    return clientId;
}
//# sourceMappingURL=google.js.map